import dayjs, { Dayjs } from 'dayjs'
import { memo, useCallback, useMemo } from 'react'
import { FieldValues, useController, UseControllerProps } from 'react-hook-form'

import ErrorMessage from '@/components/formik/messages/ErrorMessage'

import TimePickerInput, { TimePickerInputProps } from './TimePickerInput'

type TimePickerInputControllerProps<T extends FieldValues> = UseControllerProps<T> & Omit<TimePickerInputProps, 'onBlur' | 'onChange' | 'value'> & {
  disableHelperText?: boolean
  readOnly?: boolean
  disabled?: boolean
}

const MemoizedTimePickerInput = memo(TimePickerInput)

export default function TimePickerInputController<T extends FieldValues>(props: TimePickerInputControllerProps<T>) {
  const { control, disabled, disableHelperText, name, readOnly, ...rest } = props
  const {
    field,
    fieldState: { error, isTouched },
  } = useController({ name, control })

  const value = useMemo(() => (field.value === '' ? null : dayjs(field.value, 'HH:mm', true)), [field.value])

  const handleChange = useCallback(
    (newValue: Dayjs | null, inputValue?: string) => {
      field.onChange(newValue === null ? '' : newValue?.isValid() ? newValue.format('HH:mm') : inputValue || '')
    },
    [field]
  )

  return (
    <MemoizedTimePickerInput
      {...rest}
      disabled={disabled}
      error={!!error && isTouched && !disabled && !readOnly}
      helperText={!disableHelperText && !!error && isTouched ? <ErrorMessage error={error.message} /> : undefined}
      name={field.name}
      onBlur={field.onBlur}
      onChange={handleChange}
      readOnly={readOnly}
      value={value}
    />
  )
}
